import { useAppConfig } from 'application/configuration'
import { usePriceFormatter } from 'lib/use-price-formatter'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { LabeledIcon } from 'ui/labeled-icon'
import { Tooltip } from 'ui/tooltip'

import { getProjectName } from '../../../lib/get-project-name'
import { useCarDetailsCardConfig } from '../../../stages/car-details'
import { Icon } from '../../../ui/icon'

export type Props = {
  lower: null | number
  higher: null | number
  smartContractEnabled?: boolean
  disclaimer?: PriceDisclaimerProps
  showRange?: boolean
  alwaysForceRange?: boolean
  creditDiscountSwitch?: boolean
  tradeInDiscountSwitch?: boolean
  insuranceDiscountSwitch?: boolean
}

export const Price: FC<Props> = ({
  lower,
  higher,
  disclaimer,
  showRange = false,
  smartContractEnabled = false,
  alwaysForceRange = false,
  creditDiscountSwitch,
  tradeInDiscountSwitch,
  insuranceDiscountSwitch
}) => {
  const formatPrice = usePriceFormatter()

  const higherPrice = higher === null ? null : formatPrice(higher)

  let lowerPrice = lower === null ? null : formatPrice(lower)

  const disclaimerText = useMemo(() => {
    if (showRange && disclaimer?.content.range) return disclaimer.content.range
    if (!showRange && disclaimer?.content.final) return disclaimer.content.final
    return null
  }, [showRange, disclaimer])

  const { baseCarModel } = useCarDetailsCardConfig()

  const {
    i18n: {
      common: { from: fromText, to: toText }
    }
  } = useAppConfig()

  /* Если цены не переданы ничего не выыводим*/
  if (higherPrice === null && lowerPrice === null) return null

  /* Если цены равны минимальная цена*/
  if (higherPrice === lowerPrice && getProjectName() !== 'avito')
    return (
      <>
        <LowerPrice>
          {showRange && alwaysForceRange && getProjectName() !== 'avito' && <PriceRange>{fromText}</PriceRange>}{' '}
          {lowerPrice}
          {!smartContractEnabled && disclaimer && getProjectName() !== 'avito' && disclaimer.content.range && (
            <Disclaimer text={disclaimer.text} content={disclaimer.content.range} />
          )}
        </LowerPrice>
      </>
    )

  if (!creditDiscountSwitch && !tradeInDiscountSwitch && !insuranceDiscountSwitch && getProjectName() === 'avito') {
    /*Если нет ни одно скидки, выводим базовую цену для авито*/
    lowerPrice = higherPrice
  } else if (creditDiscountSwitch && !tradeInDiscountSwitch && !insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.creditDiscount)
  } else if (!creditDiscountSwitch && tradeInDiscountSwitch && !insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.tradeinDiscount)
  } else if (!creditDiscountSwitch && !tradeInDiscountSwitch && insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.insuranceDiscount)
  } else if (creditDiscountSwitch && tradeInDiscountSwitch && insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(
      higher - baseCarModel.insuranceDiscount - baseCarModel.creditDiscount - baseCarModel.tradeinDiscount
    )
  } else if (creditDiscountSwitch && tradeInDiscountSwitch && !insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.creditDiscount - baseCarModel.tradeinDiscount)
  } else if (creditDiscountSwitch && !tradeInDiscountSwitch && insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.insuranceDiscount - baseCarModel.creditDiscount)
  } else if (!creditDiscountSwitch && tradeInDiscountSwitch && insuranceDiscountSwitch && higher) {
    lowerPrice = formatPrice(higher - baseCarModel.insuranceDiscount - baseCarModel.tradeinDiscount)
  }

  return (
    <>
      {getProjectName() === 'avito' && <NoChangePrice>Цена не поменяется в салоне</NoChangePrice>}
      <LowerPrice>
        {showRange === true && getProjectName() !== 'avito' && <PriceRange>{fromText}</PriceRange>} {lowerPrice}
        {getProjectName() === 'avito' && (
          <Label>
            <WrapAbsolute className={'left-arrow'}>
              <Icon name={'arrow-green-left-avito'} size={1.1666} color={'#04E061'} />
            </WrapAbsolute>
            Рыночная цена
            <WrapAbsolute className={'right-arrow'}>
              <Icon name={'arrow-green-right-avito'} size={1.1666} color={'#04E061'} />
            </WrapAbsolute>
          </Label>
        )}
      </LowerPrice>
      {(creditDiscountSwitch || tradeInDiscountSwitch || insuranceDiscountSwitch || getProjectName() !== 'avito') && (
        <HigherPriceWrapper>
          <HigherPrice crossed={!showRange} grayedOut={true}>
            {showRange && getProjectName() !== 'avito' && toText} {higherPrice}
          </HigherPrice>
          {disclaimer && disclaimerText && getProjectName() !== 'avito' && (
            <Disclaimer text={disclaimer.text} content={disclaimerText} />
          )}
        </HigherPriceWrapper>
      )}
    </>
  )
}

export type PriceDisclaimerProps = {
  content: {
    final?: string
    range?: string
  }
  text?: string
}

const Disclaimer: FC<{ content: string; text?: string }> = ({ content, text }) => {
  return (
    <>
      <Info data-for="priceDisclaimer" data-tip={content}>
        <LabeledIcon icon={'info'} size={'small'} type={'primary'}>
          {text}
        </LabeledIcon>
      </Info>
      <Tooltip id="priceDisclaimer" />
    </>
  )
}

const PriceRange = styled.div`
  ${({ theme }) => theme.textStyles.h4};
  display: inline-block;
  font-weight: 300;
`

const LowerPrice = styled.div`
  display: ${() => (getProjectName() === 'avito' ? 'inline-flex' : 'inline-block')};
  align-items: ${() => (getProjectName() === 'avito' ? 'center' : 'flex-start')};
  justify-content: space-between;
  flex-shrink: 0;
  margin-top: 0;
  margin-right: ${({ theme }) => theme.spacing[2]};

  ${({ theme }) => theme.textStyles.h3};
  font-size: ${getProjectName() === 'avito' && '3.2rem'};
  font-weight: ${({ theme }) => (getProjectName() === 'avito' ? 800 : 300)};
  color: ${({ theme }) => theme.palette.black};
`

const HigherPriceWrapper = styled.div`
  display: flex;
  min-height: ${({ theme }) => theme.spacing[8]};
  margin-bottom: ${() => (getProjectName() === 'avito' ? '-8px' : 0)};
`

const HigherPrice = styled.div<{ crossed?: boolean; grayedOut?: boolean }>`
  flex-shrink: 0;
  margin-top: 0;
  margin-bottom: 0;
  ${({ theme }) => theme.textStyles.h5}
  font-weight: ${() => (getProjectName() === 'avito' ? 500 : 300)};
  color: ${({ theme, grayedOut = false }) => (grayedOut ? theme.palette.grey[500] : theme.palette.grey[900])};
  color: ${() => {
    return getProjectName() === 'avito' && '#5c5c5c'
  }};
  text-decoration: ${({ crossed }) =>
    crossed ? 'line-through' : getProjectName() === 'avito' ? 'line-through' : 'none'};
`

const Info = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  color: ${({ theme }) => theme.palette.brand.primary};
  ${({ theme }) => theme.textStyles.p3}
  cursor: pointer;
`

const NoChangePrice = styled.div`
  ${({ theme }) => theme.textStyles.p2}
`

const Label = styled.div`
  padding: 0;
  background: #04e061;
  ${({ theme }) => theme.typography.paragraph.p2}
  font-size: ${() => getProjectName() === 'avito' && '1.3rem'};
  margin: 0;
  color: ${({ theme }) => theme.palette.white};
  font-weight: 500;
  position: relative;
  display: flex;
  height: 28px;
  align-items: center;
`

const WrapAbsolute = styled.div`
  position: absolute;
  width: 10px;
  height: inherit;
  &.left-arrow {
    left: -19px;
  }
  &.right-arrow {
    right: -1px;
  }
`
